/**
 * Audio Panel Component for Reels video editor
 * Audio controls, music library, and audio effects
 */

import React, { useState, useCallback, useRef } from 'react';
import { Volume2, VolumeX, Music, Upload, Play, Pause, Plus, Trash2, Mic, Waves } from 'lucide-react';
import { useProject, useUI } from '../stores/projectStore';
import { getAudioMetadata, createMediaUrl, formatFileSize, generateWaveformData } from '../utils/mediaUtils';
import { AUDIO_EFFECTS, SPEED_PRESETS } from '../utils/effectPresets';

// Sample music library (would be fetched from API)
const SAMPLE_MUSIC = [
  { id: 'music-1', name: 'Upbeat Pop', duration: 120, category: 'Pop', bpm: 120 },
  { id: 'music-2', name: 'Chill Lo-Fi', duration: 180, category: 'Lo-Fi', bpm: 85 },
  { id: 'music-3', name: 'Corporate Motivational', duration: 150, category: 'Corporate', bpm: 110 },
  { id: 'music-4', name: 'Energetic EDM', duration: 90, category: 'EDM', bpm: 128 },
  { id: 'music-5', name: 'Acoustic Guitar', duration: 200, category: 'Acoustic', bpm: 95 },
  { id: 'music-6', name: 'Epic Cinematic', duration: 180, category: 'Cinematic', bpm: 100 },
];

const AudioPanel = () => {
  const { project, actions } = useProject();
  const { ui } = useUI();
  const fileInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState('mixer'); // 'mixer' | 'music' | 'effects' | 'voiceover'
  const [playingPreview, setPlayingPreview] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isRecording, setIsRecording] = useState(false);

  // Get audio tracks
  const audioTracks = project.tracks.filter(t => t.type === 'audio');

  // Get selected audio clip
  const getSelectedAudioClip = () => {
    for (const track of audioTracks) {
      const clip = track.clips.find(c => c.id === ui.selectedClipId);
      if (clip) return { track, clip };
    }
    return { track: null, clip: null };
  };

  const { track: selectedTrack, clip: selectedClip } = getSelectedAudioClip();

  // Update track volume
  const updateTrackVolume = useCallback((trackId, volume) => {
    actions.updateTrack(trackId, { volume });
  }, [actions]);

  // Toggle track mute
  const toggleTrackMute = useCallback((trackId) => {
    const track = project.tracks.find(t => t.id === trackId);
    if (track) {
      actions.updateTrack(trackId, { muted: !track.muted });
    }
  }, [project.tracks, actions]);

  // Update clip volume
  const updateClipVolume = useCallback((volume) => {
    if (!selectedClip || !selectedTrack) return;
    actions.updateClip(selectedTrack.id, selectedClip.id, { volume });
  }, [selectedClip, selectedTrack, actions]);

  // Update clip speed
  const updateClipSpeed = useCallback((speed) => {
    if (!selectedClip || !selectedTrack) return;
    actions.updateClip(selectedTrack.id, selectedClip.id, { speed });
  }, [selectedClip, selectedTrack, actions]);

  // Import audio file
  const handleFileImport = useCallback(async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    for (const file of files) {
      try {
        const metadata = await getAudioMetadata(file);
        const url = createMediaUrl(file);
        let waveform = [];

        try {
          waveform = await generateWaveformData(file);
        } catch (err) {
          console.warn('Could not generate waveform:', err);
        }

        const mediaItem = {
          id: `media-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          name: file.name,
          type: 'audio',
          file,
          url,
          size: file.size,
          duration: metadata.duration,
          waveform,
          addedAt: new Date().toISOString(),
        };

        actions.addMedia(mediaItem);

        // Add to first audio track
        if (audioTracks.length > 0) {
          const track = audioTracks[0];
          const endTime = track.clips.reduce((max, clip) => Math.max(max, clip.startTime + clip.duration), 0);

          const clip = {
            id: `clip-${Date.now()}`,
            mediaId: mediaItem.id,
            name: file.name,
            startTime: endTime,
            duration: metadata.duration,
            inPoint: 0,
            outPoint: metadata.duration,
            volume: 1,
            speed: 1,
            effects: [],
          };

          actions.addClip(track.id, clip);
        }
      } catch (error) {
        console.error('Failed to import audio:', error);
      }
    }

    e.target.value = '';
  }, [audioTracks, actions]);

  // Add sample music to timeline
  const addMusicToTimeline = useCallback((music) => {
    if (audioTracks.length === 0) return;

    const track = audioTracks[0];
    const endTime = track.clips.reduce((max, clip) => Math.max(max, clip.startTime + clip.duration), 0);

    const clip = {
      id: `clip-${Date.now()}`,
      musicId: music.id,
      name: music.name,
      startTime: endTime,
      duration: music.duration,
      inPoint: 0,
      outPoint: music.duration,
      volume: 0.8,
      speed: 1,
      effects: [],
    };

    actions.addClip(track.id, clip);
  }, [audioTracks, actions]);

  // Apply audio effect
  const applyAudioEffect = useCallback((effect) => {
    if (!selectedClip || !selectedTrack) return;

    const newEffect = {
      id: `effect-${Date.now()}`,
      type: 'audio',
      effectId: effect.id,
      ...effect,
    };

    actions.addEffect(selectedTrack.id, selectedClip.id, newEffect);
  }, [selectedClip, selectedTrack, actions]);

  // Filter music
  const filteredMusic = SAMPLE_MUSIC.filter(m =>
    m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-slate-800">
        <h3 className="text-sm font-semibold text-white">Audio</h3>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-800">
        {[
          { id: 'mixer', label: 'Mixer', icon: Volume2 },
          { id: 'music', label: 'Music', icon: Music },
          { id: 'effects', label: 'Effects', icon: Waves },
          { id: 'voiceover', label: 'Voice', icon: Mic },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-1 py-2 text-xs font-medium transition-colors ${activeTab === tab.id ? 'text-teal-300 border-b-2 border-teal-400' : 'text-slate-400 hover:text-white'}`}
          >
            <tab.icon className="w-3 h-3" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-auto p-4">
        {activeTab === 'mixer' && (
          <div className="space-y-4">
            {/* Track mixers */}
            {audioTracks.map((track) => (
              <div key={track.id} className="p-3 bg-slate-800 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-white">{track.name}</span>
                  <button
                    onClick={() => toggleTrackMute(track.id)}
                    className={`p-1.5 rounded ${track.muted ? 'bg-red-600' : 'bg-slate-700 hover:bg-slate-600'}`}
                  >
                    {track.muted ? <VolumeX className="w-4 h-4 text-white" /> : <Volume2 className="w-4 h-4 text-white" />}
                  </button>
                </div>
                <div className="flex items-center gap-3">
                  <Volume2 className="w-4 h-4 text-slate-500" />
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={track.volume || 1}
                    onChange={(e) => updateTrackVolume(track.id, Number(e.target.value))}
                    className="flex-1"
                    disabled={track.muted}
                  />
                  <span className="text-xs text-slate-400 w-10 text-right">
                    {Math.round((track.volume || 1) * 100)}%
                  </span>
                </div>

                {/* Clips on this track */}
                {track.clips.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-700">
                    <div className="text-xs text-slate-500 mb-2">Clips</div>
                    {track.clips.map((clip) => (
                      <div
                        key={clip.id}
                        className={`flex items-center justify-between p-2 rounded ${ui.selectedClipId === clip.id ? 'bg-teal-600/20' : 'hover:bg-slate-700'}`}
                      >
                        <span className="text-xs text-white truncate flex-1">{clip.name}</span>
                        <span className="text-xs text-slate-500">{clip.duration.toFixed(1)}s</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Selected clip controls */}
            {selectedClip && (
              <div className="p-3 bg-slate-800 rounded-lg mt-4">
                <div className="text-sm text-white mb-3">Clip: {selectedClip.name}</div>

                {/* Volume */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs text-slate-400">Volume</label>
                    <span className="text-xs text-slate-500">{Math.round((selectedClip.volume || 1) * 100)}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="2"
                    step="0.01"
                    value={selectedClip.volume || 1}
                    onChange={(e) => updateClipVolume(Number(e.target.value))}
                    className="w-full"
                  />
                </div>

                {/* Speed */}
                <div>
                  <label className="text-xs text-slate-400 mb-2 block">Speed</label>
                  <div className="grid grid-cols-4 gap-1">
                    {SPEED_PRESETS.slice(0, 8).map((preset) => (
                      <button
                        key={preset.value}
                        onClick={() => updateClipSpeed(preset.value)}
                        className={`px-2 py-1 text-xs rounded ${(selectedClip.speed || 1) === preset.value ? 'bg-teal-600 text-white' : 'bg-slate-700 text-slate-400 hover:text-white'}`}
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'music' && (
          <div className="space-y-4">
            {/* Import button */}
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-white rounded-lg transition-colors"
            >
              <Upload className="w-4 h-4" />
              Import Audio
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*"
              multiple
              onChange={handleFileImport}
              className="hidden"
            />

            {/* Search */}
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search music..."
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-teal-500"
            />

            {/* Music library */}
            <div className="space-y-2">
              {filteredMusic.map((music) => (
                <div
                  key={music.id}
                  className="flex items-center gap-3 p-3 bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors group"
                >
                  <button
                    onClick={() => setPlayingPreview(playingPreview === music.id ? null : music.id)}
                    className="p-2 bg-slate-700 rounded-full hover:bg-teal-600"
                  >
                    {playingPreview === music.id ? <Pause className="w-4 h-4 text-white" /> : <Play className="w-4 h-4 text-white" />}
                  </button>

                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{music.name}</div>
                    <div className="text-xs text-slate-500">
                      {music.category} - {Math.floor(music.duration / 60)}:{(music.duration % 60).toString().padStart(2, '0')} - {music.bpm} BPM
                    </div>
                  </div>

                  <button
                    onClick={() => addMusicToTimeline(music)}
                    className="p-2 bg-teal-600 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-teal-500 transition-all"
                  >
                    <Plus className="w-4 h-4 text-white" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'effects' && (
          <div className="space-y-4">
            {!selectedClip ? (
              <div className="text-center py-8">
                <Waves className="w-12 h-12 text-slate-700 mx-auto mb-3" />
                <p className="text-slate-400 text-sm">Select an audio clip to apply effects</p>
              </div>
            ) : (
              <>
                <div className="text-sm text-white mb-2">Clip: {selectedClip.name}</div>

                {/* Effect categories */}
                {['fade', 'effect', 'eq', 'cleanup'].map((category) => {
                  const categoryEffects = AUDIO_EFFECTS.filter(e => e.category === category);
                  if (categoryEffects.length === 0) return null;

                  return (
                    <div key={category}>
                      <h4 className="text-xs text-slate-400 mb-2 capitalize">{category}</h4>
                      <div className="grid grid-cols-2 gap-2">
                        {categoryEffects.map((effect) => (
                          <button
                            key={effect.id}
                            onClick={() => applyAudioEffect(effect)}
                            className="p-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-xs text-white text-left transition-colors"
                          >
                            {effect.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        )}

        {activeTab === 'voiceover' && (
          <div className="space-y-4">
            <div className="text-center py-8">
              <div className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${isRecording ? 'bg-red-600 animate-pulse' : 'bg-slate-800'}`}>
                <Mic className={`w-8 h-8 ${isRecording ? 'text-white' : 'text-slate-400'}`} />
              </div>

              <button
                onClick={() => setIsRecording(!isRecording)}
                className={`px-6 py-2.5 rounded-lg font-medium transition-colors ${isRecording ? 'bg-red-600 hover:bg-red-500 text-white' : 'bg-teal-600 hover:bg-teal-500 text-white'}`}
              >
                {isRecording ? 'Stop Recording' : 'Start Recording'}
              </button>

              <p className="text-xs text-slate-500 mt-4">
                Record a voiceover directly in the browser. Your recording will be added to the audio track.
              </p>
            </div>

            {/* Recording tips */}
            <div className="p-3 bg-slate-800 rounded-lg">
              <h4 className="text-sm text-white mb-2">Recording Tips</h4>
              <ul className="text-xs text-slate-400 space-y-1">
                <li>- Use a quiet environment</li>
                <li>- Keep microphone 6-12 inches away</li>
                <li>- Speak clearly and at a steady pace</li>
                <li>- Do a test recording first</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AudioPanel;
